/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useEffect, useState } from 'react';
import { Button, Card, Col, Container, OverlayTrigger, Row, Spinner, Table, Tooltip } from 'react-bootstrap';
import _ from 'lodash';
import useReports from '../../hooks/api/report/useReports';
import ModalComponent from '../../components/modal/ModalComponent';
import { FormattedMessage, useIntl } from 'react-intl';
import PageTitle from '../../components/layout/pageTitle/PageTitle';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { Report, ReportContent, ReportStep } from '../../services/report/ReportService';
import { v4 as uuidv4 } from 'uuid';
import useReport from '../../hooks/api/report/useReport';
import EmptyList from '../../components/layout/emptyList/EmptyList';
import LoadingSpinner from '../../components/layout/loadingSpinner/LoadingSpinner';
import { useHistory, useParams } from 'react-router';
import { getSecuritySection, SecuritySections, SecurityModel } from '../../models/security/security';
import { FallbackPage } from '../../components/layout/navbar/Navbar';
import { RouteType } from '../../routes/routes';

type Props = { route: RouteType };

const SavedReportsPage: FC<Props> = ({ route }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [selectedReportId, setSelectedReportId] = useState<string>('');
  const [reportSecurity, setReportSecurity] = useState<SecurityModel | null>(null);
  const [downloadingReportIndex, setDownloadingReportIndex] = useState<number | null>(null);
  const { reports, setFormId, setOrganizationId, deleteReport, reloadReports, isLoading } = useReports();
  const { generateReport, setOrganizationId: setId } = useReport();
  const security = useSelector((state: RootState) => state.organization.organization?.security);
  const organizationId = useSelector((state: RootState) => state.organization.organization?.id) as string;
  const { formId } = useParams<any>();
  const intl = useIntl();
  const history = useHistory();
  const handleDelete = async () => {
    await deleteReport(organizationId, formId, selectedReportId);
    await reloadReports();
    setSelectedReportId('');
    setShowDeleteConfirm(false);
  };

  useEffect(() => {
    if (formId) {
      setFormId(formId);
    }
  }, [formId]);

  useEffect(() => {
    if (organizationId) {
      setOrganizationId(organizationId);
      setId(organizationId);
    }
  }, [organizationId]);

  useEffect(() => {
    if (security) {
      setReportSecurity(getSecuritySection(security, SecuritySections.REPORT));
    }
    if (reportSecurity && !reportSecurity?.read) {
      history.push(FallbackPage);
    }
  }, [security, reportSecurity]);

  const handleGenerateReport = async (report: any, index: number) => {
    setDownloadingReportIndex(index);
    const reportSteps: ReportStep[] = [];
    const steps = report.content.steps;
    _.forEach(steps, (step) => {
      const stepFields = _.map(step.fields, (val) => ({
        id: val.id,
      }));

      reportSteps.push({
        id: step.id,
        fields: stepFields,
      });
    });

    const content: ReportContent = {
      name: report.content.name,
      steps: reportSteps,
      general: report.content.general,
    };
    const reportToGenerate: Partial<Report> = { content, formId, culture: intl.locale };

    const result = await generateReport(organizationId, formId, { ...reportToGenerate, id: uuidv4() } as Report);
    setDownloadingReportIndex(null);
  };

  const renderTooltip = (text) => <Tooltip id="button-tooltip">{text}</Tooltip>;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container className="saved-reports" fluid>
          <PageTitle title={intl.formatMessage({ id: 'navbar.settings.reports.title' })} backRoute={route}></PageTitle>
          <Row>
            <Col xl={12} className="text-end pe-1 mb-2">
              <Button onClick={() => history.push(`/reports/${formId}/create`)} disabled={!reportSecurity?.write}>
                <span className="mdi mdi-plus pe-1"></span>
                <span>{intl.formatMessage({ id: 'reports.addReport' })}</span>
              </Button>
            </Col>
            <Col xl={12}>
              <Card>
                {isLoading && <LoadingSpinner />}
                {!isLoading && reports && reports.length > 0 && (
                  <div>
                    <Card.Body>
                      <Table>
                        <thead>
                          <tr>
                            <th className="text-start">
                              <FormattedMessage id={'reports.reportName'}></FormattedMessage>
                            </th>
                            <th className="text-end action-button-column-report">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reports &&
                            _.map(reports, (report, index) => {
                              return (
                                <tr key={index}>
                                  <td className="report-name">{report.content.name}</td>
                                  <td className="text-end">
                                    <OverlayTrigger
                                      delay={{ show: 50, hide: 100 }}
                                      overlay={renderTooltip(intl.formatMessage({ id: 'reports.editReport' }))}
                                      placement="top"
                                    >
                                      <Button
                                        variant="primary"
                                        className="d-inline-flex align-items-center justify-content-center me-1"
                                        onClick={() => history.push(`/reports/${formId}/${report.id}`)}
                                        disabled={!reportSecurity?.update}
                                      >
                                        <span className="mdi mdi-pencil-outline"></span>
                                      </Button>
                                    </OverlayTrigger>
                                    <OverlayTrigger
                                      delay={{ show: 50, hide: 100 }}
                                      overlay={renderTooltip(intl.formatMessage({ id: 'reports.downloadReport' }))}
                                      placement="top"
                                    >
                                      <Button
                                        className="d-inline-flex align-items-center justify-content-center me-1"
                                        onClick={() => handleGenerateReport(report, index)}
                                        disabled={!reportSecurity?.read}
                                      >
                                        {downloadingReportIndex == index ? (
                                          <Spinner animation="border" size="sm" />
                                        ) : (
                                          <span className="mdi mdi-download"></span>
                                        )}
                                      </Button>
                                    </OverlayTrigger>
                                    <OverlayTrigger
                                      delay={{ show: 50, hide: 100 }}
                                      overlay={renderTooltip(intl.formatMessage({ id: 'reports.deleteReport' }))}
                                      placement="top"
                                    >
                                      <Button
                                        variant="primary"
                                        className="d-inline-flex align-items-center justify-content-center me-1"
                                        onClick={() => {
                                          setSelectedReportId(report.id);
                                          setShowDeleteConfirm(true);
                                        }}
                                        disabled={!reportSecurity?.delete}
                                      >
                                        <span className="mdi mdi-trash-can-outline"></span>
                                      </Button>
                                    </OverlayTrigger>
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </div>
                )}
                {!isLoading && reports && reports.length == 0 && (
                  <div className="d-flex flex-column justify-content-center align-items-center p-4">
                    <EmptyList message={intl.formatMessage({ id: 'reports.noSavedReports' })} />
                  </div>
                )}
              </Card>
            </Col>
          </Row>
        </Container>
        <ModalComponent
          closeSettings={() => setShowDeleteConfirm(false)}
          buttons={[
            {
              text: intl.formatMessage({ id: 'reports.cancel' }),
              buttonVariant: 'secondary',
              onClick: () => setShowDeleteConfirm(false),
            },
            {
              text: intl.formatMessage({ id: 'reports.delete' }),
              buttonVariant: 'danger',
              onClick: handleDelete,
            },
          ]}
          show={showDeleteConfirm}
          modalHeading={intl.formatMessage({ id: 'reports.deleteModal' })}
          modalSize="lg"
        >
          <div className="d-flex justify-content-center flex-column">
            <label className="modal-label">
              <b>{intl.formatMessage({ id: 'reports.deleteReports' })}</b>
            </label>
          </div>
        </ModalComponent>
      </div>
    </React.Fragment>
  );
};

export default SavedReportsPage;
